import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline"
import React, { useState, useRef, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const FAQSection = ({ section, theme, highlightedField }) => {
  const [expanded, setExpanded] = useState({})
  const [animatingItems, setAnimatingItems] = useState({})
  const timeoutRefs = useRef({})

  const isHighlighted = highlightedField === `section-${section.id}`

  // Get FAQ data from section settings
  const faqItems = section?.settings?.faqs || []

  // Get section settings
  const settings = section?.settings || {}
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = showBackgroundColor && settings.backgroundColor ? settings.backgroundColor : null
  const textColor = settings.textColor || theme?.colors?.textDark || "#1F2937"

  useEffect(
    () => () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout)
    },
    []
  )

  const handleToggle = (question) => {
    setExpanded((prev) => {
      const newExpanded = { ...prev, [question]: !prev[question] }
      if (!newExpanded[question]) {
        setAnimatingItems((prev) => ({ ...prev, [question]: true }))
        timeoutRefs.current[question] = setTimeout(() => {
          setAnimatingItems((prev) => ({ ...prev, [question]: false }))
        }, 300)
      }
      return newExpanded
    })
  }

  // Always render the section, even if no FAQs

  return (
    <section
      className={twMerge("py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: backgroundColor || theme?.colors?.bgLight || "#FFFFFF" }}>
      <AnimatedSection className="mx-auto max-w-4xl px-4">
        <div className="space-y-8">
          {section.headline && (
            <h2
              className="text-center text-3xl font-bold lg:text-4xl"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: textColor
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="text-center text-lg lg:text-xl"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: textColor,
                opacity: 0.8
              }}>
              {section.subheadline}
            </p>
          )}

          <div className="space-y-4">
            {faqItems && faqItems.length > 0 ? (
              faqItems.map((item, index) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex cursor-pointer items-center gap-3" onClick={() => handleToggle(item.question)}>
                    {expanded[item.question] || animatingItems[item.question] ? (
                      <MinusIcon className="h-5 w-5 flex-none text-teal" />
                    ) : (
                      <PlusIcon className="h-5 w-5 flex-none text-teal" />
                    )}
                    <div
                      className="text-lg font-medium hover:text-teal"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "sans-serif",
                        color: textColor
                      }}>
                      {item.question}
                    </div>
                  </div>
                  <div
                    className={`ml-8 overflow-hidden transition-all duration-300 ease-in-out ${
                      expanded[item.question] ? "max-h-96" : "max-h-0"
                    }`}>
                    <div
                      className="prose prose-lg max-w-none"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "sans-serif",
                        color: textColor,
                        opacity: 0.8
                      }}
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <p
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "sans-serif",
                    color: textColor,
                    opacity: 0.6
                  }}>
                  No frequently asked questions have been added yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

export default FAQSection
